import React, { Component } from 'react';
import moment from "moment";
import numeral from 'numeral';
import { connect } from 'react-redux';
import {List, Map, fromJS} from "immutable";
import {Icon} from "@blueprintjs/core";
import _ from "lodash";
import Modal from "@atlaskit/modal-dialog/dist/esm/components/Modal";
import {isFloat} from 'validator';

// Components
import DateInput from "../../../components/common/date-input";
import Select from "../../../components/common/virtualized-select";
import ReceiptLinesTable from "../../../components/common/ReceiptLinesTable";
import SelectProductDialog from "../../../components/dialogs/SelectProductDialog";

// Actions
import {getPurchaseOrders, cleanPurchaseOrders} from "../../../actions/purchase-orders";
import {getSuppliers, cleanSuppliers} from "../../../actions/suppliers";
import {getWarehouses, cleanWarehouses} from "../../../actions/warehouses";
import {getProducts, cleanProducts} from "../../../actions/products";

// Numeral configuration
import 'numeral/locales/es-es';
numeral.locale('es-es');
numeral.defaultFormat('0,0.00 $');

class ReceiptDetail extends Component {
    constructor(props) {
        super(props);

        this.state = {
            canNewLine: false,
            // Modal
            modalIsOpen: false,
            modalType: null,
            canSelectProduct: false,
            // object fields
            receipt_code: '',
            delivery_date: moment(),
            orders: [],
            purchaseOrders: List([]),
            supplier: -1,
            supplier_receipt_code: '',
            comments: '',
            lines: List([]),
            // lines
            product: -1,
            warehouse: -1,
            amount: 0,
            price: 0,
            discount: 0,
            vat: 0,
            total: 0,
            // Statistics
            orderSubtotal: numeral(0).format(),
            orderVat: numeral(0).format(),
            orderTotal: numeral(0).format()
        }
    }
    componentDidMount() {
        this.updateReceiptsState();
        this.props.onRef(this);
    }
    componentDidUpdate(prevProps, prevState) {
        if (this.props.receipt !== prevProps.receipt) {
            this.updateReceiptsState();
        }

        // Calculate Statistics
        if (this.state.lines !== prevState.lines) {
            let orderSubtotal = 0, orderVat = 0, orderTotal = 0;

            this.state.lines.forEach(line => {
                let subtotalCost, discount, subtotal, vat;

                subtotalCost = _.round((line.get('amount') * line.get('price')), 2);
                discount = _.round((subtotalCost * (line.get('discount') / 100)), 2);
                subtotal = subtotalCost - discount;
                vat = subtotal * (line.get('vat') / 100);

                orderSubtotal = orderSubtotal + subtotal;
                orderVat = orderVat + vat;
                orderTotal = orderTotal + line.get('total');

            });

            this.setState({
                orderSubtotal: numeral(orderSubtotal).format(),
                orderVat: numeral(orderVat).format(),
                orderTotal: numeral(orderTotal).format()
            });
        }

        // On Edit Mode
        if (this.props.editMode !== prevProps.editMode && this.props.editMode) {
            this.props.getPurchaseOrders({
                filters: {
                    supplier: this.props.receipt.getIn(['supplier', '_id']),
                    $or: [{
                        status: 'partial'
                    }, {
                        status: 'received'
                    }]
                },
                projection: 'order_code',
                deleteVirtuals: true
            });
            this.props.getSuppliers({
                filters: { status: 'active' },
                projection: 'supplier_code basic_info.name',
                deleteVirtuals: true
            });
            this.props.getWarehouses({
                filters: { status: 'active' },
                projection: 'warehouse_code name',
                deleteVirtuals: true
            });
            this.props.getProducts({
                filters: {
                    'suppliers.supplier': this.props.receipt.getIn(['supplier', '_id']),
                    status: 'active'
                },
                projection: 'product_code name',
                deleteVirtuals: true
            });
        }

        // Handle purchaseOrders
        if (this.props.purchaseOrders !== prevProps.purchaseOrders && this.props.purchaseOrders instanceof List) {
            if (!(this.props.purchaseOrders.get(0) && this.props.purchaseOrders.get(0).get('lines'))) {
                let purchaseOrders = [];
                const currentPurchaseOrders = this.state.orders;
                const availablePurchaseOrders = this.props.purchaseOrders;
                if (currentPurchaseOrders.length > 0) {
                    for (const order of currentPurchaseOrders) {
                        purchaseOrders.push(order);
                    }
                    purchaseOrders = fromJS(purchaseOrders);
                    for (const order of availablePurchaseOrders) {
                        const orderFound = purchaseOrders.find(o => o.get("_id") === order.get("_id"));
                        if (!orderFound) {
                            purchaseOrders = purchaseOrders.push(order);
                        }
                    }
                } else {
                    purchaseOrders = availablePurchaseOrders;
                }
                this.setState({
                    purchaseOrders
                });
            }
            else {
                let lines = List([]);
                this.props.purchaseOrders.forEach(order => {
                    order.get('lines').forEach(line => {
                        lines = lines.push(line);
                    });
                });
                this.setState({
                    lines,
                    product: -1,
                    warehouse: -1,
                    amount: 0,
                    price: 0,
                    discount: 0,
                    vat: 0,
                    total: 0
                }, this.validateReceipt);
            }
        }

    }
    componentWillUnmount() {
        this.props.cleanSuppliers();
        this.props.cleanWarehouses();
        this.props.cleanProducts();
        this.props.onRef(undefined);
    }

    updateReceipt = () => {
        let lines = new List([]);

        this.state.lines.forEach(line => {
            lines = lines.insert(lines.size, line
                .set('product', line.getIn(['product', '_id']))
                .set('warehouse', line.getIn(['warehouse', '_id']))
                .delete('_id')
                .delete('total')
            )
        });

        this.props.updateReceipt(this.props.receipt.get('_id'), {
            // object fields
            delivery_date: this.state.delivery_date,
            orders: this.state.orders.map(order => order._id),
            supplier: this.state.supplier,
            supplier_receipt_code: this.state.supplier_receipt_code,
            comments: this.state.comments,
            lines
        });
    };
    updateReceiptsState = () => {
        this.setState({
            // object fields
            receipt_code: this.props.receipt.get('receipt_code'),
            delivery_date: moment(this.props.receipt.get('delivery_date')),
            orders: this.props.receipt.get('orders').toJS(),
            supplier: this.props.receipt.getIn(['supplier','_id']),
            supplier_receipt_code: this.props.receipt.get('supplier_receipt_code'),
            comments: this.props.receipt.get('comments'),
            lines: this.props.receipt.get('lines'),
            orderSubtotal: numeral(this.props.receipt.getIn(['statistics', 'subtotal'])).format(),
            orderVat: numeral(this.props.receipt.getIn(['statistics', 'vat'])).format(),
            orderTotal: numeral(this.props.receipt.getIn(['statistics', 'total'])).format()
        })
    };
    replaceLines = () => {

        const orderIds = this.state.orders.map(order => order._id);

        if (orderIds.length <= 0) {
            this.setState({
                lines: List([]),
                product: -1,
                warehouse: -1,
                amount: 0,
                price: 0,
                discount: 0,
                vat: 0,
                total: 0
            }, this.validateReceipt);
        }
        else {
            this.props.getPurchaseOrders({
                filters: {
                    _id: { $in: orderIds }
                },
                projection: 'lines',
                populateRefs: true,
                calculateStatistics: 'receipts'
            });
        }
    };
    deleteLine = index => {
        this.setState({
            lines: this.state.lines.delete(index)
        }, this.validateReceipt);
    };
    validateLine = () => {
        if (
            this.state.product !== -1 &&
            this.state.warehouse !== -1 &&
            this.state.amount !== 0
        ) {
            this.setState({ canNewLine: true }, this.validateReceipt);
        } else {
            this.setState({ canNewLine: false }, this.validateReceipt);
        }
    };
    validateReceipt = () => {
        if (
            this.state.delivery_date.isValid() &&
            this.state.orders.length > 0 &&
            this.state.supplier !== -1
        ) {
            this.props.canSubmit(true);
        } else {
            this.props.canSubmit(false);
        }
    };

    // Modal handlers
    onCanSelectProduct = (canSelectProduct) => {
        this.setState({
            canSelectProduct
        });
    };
    openModal = (modalType) => {
        this.setState({
            modalType,
            modalIsOpen: true
        });
    };
    setProductState = (product) => {
        this.setState({
            product
        });
    };
    closeModal = () => {
        this.setState({
            modalType: null,
            modalIsOpen: false
        })
    };

    render() {
        let receiptDetail;

        // Modal Area
        let modal = null;
        if (this.state.modalIsOpen) {
            switch (this.state.modalType) {
                case 'selectProductDialog':
                    modal = {
                        width: 'medium',
                        title: 'Seleccionar producto',
                        body: (
                            <SelectProductDialog
                                onRef={ref => (this.child = ref)}
                                onCanSelectProduct={this.onCanSelectProduct}
                                onCloseModal={this.closeModal}
                                products={this.props.products}
                                setProductState={this.setProductState}
                            />
                        ),
                        footer: (
                            <div className="modal-footer">
                                <button
                                    key="cancel"
                                    type="button"
                                    className="btn btn-cancel"
                                    label={'Cancelar'}
                                    style={{
                                        color: 'gray',
                                        marginRight: 15
                                    }}
                                    onClick={this.closeModal}
                                >
                                    {'Cancelar'}
                                </button>
                                <button
                                    key="select"
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={!this.state.canSelectProduct}
                                    onClick={ () => this.child.onSelectButtonClick() }
                                >
                                    {'Seleccionar'}
                                </button>
                            </div>
                        )
                    };
                    break;
                default:
                    modal = {
                        title: '',
                        body: null,
                        footer: null
                    };
                    break;
            }
        }

        if (!this.props.editMode) {
            receiptDetail = (
                <div className="panel-body" >
                    <div className="row">
                        <div className="col-3">
                            <div className="form-group">
                                <label>Código de albaran de entrada *</label>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={this.props.receipt.get('receipt_code')}
                                    disabled={true}
                                />
                            </div>
                        </div>
                        <div className="col-3">
                            <div className="form-group">
                                <label>Fecha de entrega *</label>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={moment(this.props.receipt.get('delivery_date')).format('DD/MM/YYYY')}
                                    disabled={true}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row" style={{ paddingBottom: 16 }} >
                        <div className="col-6">
                            <label>Proveedor *</label>
                            <input
                                className="form-control"
                                type="text"
                                value={this.props.receipt.getIn(['supplier', 'basic_info', 'name'])}
                                disabled={true}
                            />
                        </div>
                        <div className="col-6">
                            <label>Código de albaran del proveedor</label>
                            <input
                                className="form-control"
                                type="text"
                                value={this.props.receipt.get('supplier_receipt_code')}
                                disabled={true}
                            />
                        </div>
                    </div>
                    <div className="row" style={{ paddingBottom: 16 }}>
                        <div className="col-12">
                            <label>Pedidos de compra asociados *</label>
                            <input
                                className="form-control"
                                type="text"
                                value={this.state.orders.map(order => ' ' + order.order_code)}
                                disabled={true}
                            />
                        </div>
                    </div>
                    <div className="row" style={{ paddingBottom: 16 }} >
                        <div className="col-12">
                            <label>Comentarios</label>
                            <input
                                className="form-control"
                                type="text"
                                value={this.props.receipt.get('comments')}
                                disabled={true}
                            />
                        </div>
                    </div>
                    <h4>Lineas de albaran</h4>
                    <ReceiptLinesTable
                        receiptLines={this.props.receipt.get('lines')}
                        deleteLine={this.deleteLine}
                    />
                    <div className="row stats-zone d-flex justify-content-end" style={{  }} >
                        <div className="col-auto">
                            <span className="label ">{ numeral(this.props.receipt.getIn(['statistics', 'subtotal'])).format() }</span>
                            <span>Subtotal</span>
                        </div>
                        <div className="col-auto">
                            <span className="label ">{ numeral(this.props.receipt.getIn(['statistics', 'vat'])).format() }</span>
                            <span>IVA</span>
                        </div>
                        <div className="col-auto">
                            <span className="label ">{ numeral(this.props.receipt.getIn(['statistics', 'total'])).format() }</span>
                            <span>Total</span>
                        </div>
                    </div>
                </div>
            );
        } else {
            receiptDetail = (
                <div className="panel-body" >
                    <div className="row">
                        <div className="col-3">
                            <div className="form-group">
                                <label>Código de albaran de entrada *</label>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={this.props.receipt.get('receipt_code')}
                                    disabled={true}
                                />
                            </div>
                        </div>
                        <div className="col-3">
                            <div className="form-group">
                                <label>Fecha de entrega *</label>
                                <DateInput
                                    onChange={(selectedDate) => {
                                        this.setState({
                                            delivery_date: selectedDate
                                        }, this.validateReceipt);
                                    }}
                                    value={this.state.delivery_date}
                                    inputWrapperStyle={{height: 40}}
                                    inputStyle={{height: 37, borderRadius: 0, border: '1px solid #ccc'}}
                                    iconStyle={{top: 9, right: 12}}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row" style={{ paddingBottom: 16 }} >
                        <div className="col-6">
                            <label>Proveedor *</label>
                            <Select
                                className="virtualized-select"
                                options={this.props.suppliers.toJS()}
                                value={this.state.supplier}
                                labelKey="select"
                                valueKey="_id"
                                matchProp="select"
                                multi={false}
                                searchable={true}
                                clearable={false}
                                onChange={(supplier) => {
                                    if (supplier && supplier._id !== this.state.supplier) {
                                        if (supplier._id !== -1) {
                                            this.props.getProducts({
                                                filters: {
                                                    'suppliers.supplier': supplier._id,
                                                    status: 'active'
                                                },
                                                projection: 'product_code name',
                                                deleteVirtuals: true
                                            });
                                            this.props.getPurchaseOrders({
                                                filters: {
                                                    supplier: supplier._id,
                                                    $or: [{
                                                        status: 'partial'
                                                    }, {
                                                        status: 'received'
                                                    }]
                                                },
                                                projection: 'order_code',
                                                deleteVirtuals: true
                                            });
                                        }
                                        this.setState({
                                            supplier: supplier._id,
                                            supplier_receipt_code: '',
                                            orders: [],
                                            comments: '',
                                            lines: List([]),
                                            product: -1,
                                            warehouse: -1,
                                            amount: 0,
                                            price: 0,
                                            discount: 0,
                                            vat: 0,
                                            total: 0,
                                        }, this.validateReceipt);
                                    }
                                }}
                            />
                        </div>
                        <div className="col-6">
                            <label>Código de albaran del proveedor</label>
                            <input
                                className="form-control"
                                type="text"
                                value={this.state.supplier_receipt_code}
                                onChange={ (event) => {
                                    this.setState({
                                        supplier_receipt_code: event.target.value
                                    }, this.validateReceipt);
                                }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <label>Pedidos de compra asociados *</label>
                            <Select
                                className="virtualized-select"
                                options={this.state.purchaseOrders.toJS()}
                                value={this.state.orders}
                                labelKey="order_code"
                                valueKey="_id"
                                matchProp="order_code"
                                multi={true}
                                placeholder={this.state.supplier === -1 ? "Selecciona un proveedor" : "Selecciona un pedido de compra"}
                                disabled={this.state.supplier === -1}
                                searchable={true}
                                clearable={false}
                                removeSelected={false}
                                closeOnSelect={false}
                                onChange={(selectedValues) => this.setState({ orders: selectedValues }, this.validateReceipt)}
                            />
                        </div>
                        <div className="col-6" style={{ paddingTop: 25 }}>
                            <button
                                type="button"
                                className="btn btn-custom"
                                onClick={ this.replaceLines }
                            >
                                Actualizar lineas de albarán
                            </button>
                            <Icon
                                icon="info-sign"
                                style={{ margin: '-7px 10px', color: '#000' }}
                                title="Reemplaza las lineas actuales por las lineas de los pedidos seleccionados"
                            />
                        </div>
                    </div>
                    <div className="row" style={{ paddingBottom: 16 }} >
                        <div className="col-12">
                            <label>Comentarios</label>
                            <input
                                className="form-control"
                                type="text"
                                value={this.state.comments}
                                onChange={ (event) => {
                                    this.setState({
                                        comments: event.target.value
                                    }, this.validateReceipt);
                                }}
                            />
                        </div>
                    </div>
                    <h4>Lineas de albaran</h4>
                    <div className="row" style={{ paddingBottom: 16 }} >
                        <div className="col-3">
                            <label>Producto *</label>
                            <Select
                                className="virtualized-select"
                                options={this.props.products.toJS()}
                                value={this.state.product}
                                labelKey="select"
                                valueKey="_id"
                                matchProp="select"
                                multi={false}
                                searchable={true}
                                clearable={false}
                                onChange={(product) => {
                                    if (product)  {
                                        this.setState({
                                            product: product._id
                                        }, this.validateLine);
                                    }
                                }}
                                disabled={this.state.supplier === -1}
                            />
                        </div>
                        <div className="col-3">
                            <label>Almacén destino *</label>
                            <Select
                                className="virtualized-select"
                                options={this.props.warehouses.toJS()}
                                value={this.state.warehouse}
                                labelKey="select"
                                valueKey="_id"
                                matchProp="select"
                                multi={false}
                                searchable={true}
                                clearable={false}
                                onChange={(warehouse) => {
                                    if (warehouse) {
                                        this.setState({
                                            warehouse: warehouse._id
                                        }, this.validateLine);
                                    }
                                }}
                                disabled={this.state.supplier === -1}
                            />
                        </div>
                        <div className="col">
                            <label>Cantidad</label>
                            <input
                                className="form-control"
                                type="text"
                                value={this.state.amount}
                                onChange={ (event) => {
                                    let amount = event.target.value;
                                    let subtotalCost, discount, subtotal, vat, total;

                                    if (amount === '') amount = '0';
                                    if (isFloat(amount)) {
                                        subtotalCost = _.round(amount, 5) * _.round(this.state.price, 5);
                                        discount = subtotalCost * (this.state.discount / 100);
                                        subtotal = subtotalCost - discount;
                                        vat = subtotal * (this.state.vat / 100);
                                        total = subtotal + vat;

                                        this.setState({
                                            amount,
                                            total: _.round(total, 2)
                                        }, this.validateLine);
                                    }

                                }}
                                disabled={this.state.product === -1}
                            />
                        </div>
                        <div className="col">
                            <label>Precio</label>
                            <input
                                className="form-control"
                                type="text"
                                value={this.state.price}
                                onChange={ (event) => {
                                    let price = event.target.value;
                                    let subtotalCost, discount, subtotal, vat, total;

                                    if (price === '') price = '0';
                                    if (isFloat(price)) {
                                        subtotalCost = _.round(this.state.amount, 5) * _.round(price, 5);
                                        discount = subtotalCost * (this.state.discount / 100);
                                        subtotal = subtotalCost - discount;
                                        vat = subtotal * (this.state.vat / 100);
                                        total = subtotal + vat;

                                        this.setState({
                                            price,
                                            total: _.round(total, 2)
                                        });
                                    }
                                }}
                                disabled={this.state.product === -1}
                            />
                        </div>
                        <div className="col">
                            <label>Descuento (%)</label>
                            <input
                                className="form-control"
                                type="text"
                                value={this.state.discount}
                                onChange={ (event) => {
                                    let discount = event.target.value;
                                    let subtotalCost, discountLine, subtotal, vat, total;

                                    if (discount === '') discount = '0';
                                    if (isFloat(discount)) {
                                        subtotalCost = _.round(this.state.amount, 5) * _.round(this.state.price, 5);
                                        discountLine = subtotalCost * (discount / 100);
                                        subtotal = subtotalCost - discountLine;
                                        vat = subtotal * (this.state.vat / 100);
                                        total = subtotal + vat;

                                        this.setState({
                                            discount,
                                            total: _.round(total, 2)
                                        });
                                    }
                                }}
                                disabled={this.state.product === -1}
                            />
                        </div>
                        <div className="col">
                            <label>IVA (%)</label>
                            <input
                                className="form-control"
                                type="text"
                                value={this.state.vat}
                                onChange={ (event) => {
                                    let vat = event.target.value;
                                    let subtotalCost, discount, subtotal, vatLine, total;

                                    if (vat === '') vat = '0';
                                    if (isFloat(vat)) {
                                        subtotalCost = _.round(this.state.amount, 5) * _.round(this.state.price, 5);
                                        discount = subtotalCost * (this.state.discount / 100);
                                        subtotal = subtotalCost - discount;
                                        vatLine = subtotal * (vat / 100);
                                        total = subtotal + vatLine;

                                        this.setState({
                                            vat,
                                            total: _.round(total, 2)
                                        });
                                    }
                                }}
                                disabled={this.state.product === -1}
                            />
                        </div>
                        <div className="col">
                            <label>Total</label>
                            <input
                                className="form-control"
                                type="text"
                                value={this.state.total}
                                disabled={true}
                            />
                        </div>
                        <div className="col-auto" style={{ paddingTop: 23 }} >
                            <button
                                className="btn btn-custom"
                                disabled={!this.state.canNewLine}
                                onClick={() => {
                                    let lines = new List(this.state.lines);
                                    let index = this.props.products.findIndex( product => product.get('_id') === this.state.product );
                                    const product = this.props.products.get(index);
                                    index = this.props.warehouses.findIndex( warehouse => warehouse.get('_id') === this.state.warehouse );
                                    const warehouse = this.props.warehouses.get(index);

                                    lines = lines.insert(lines.size, Map({
                                        _id: lines.size,
                                        product,
                                        warehouse,
                                        amount: _.round(this.state.amount, 5),
                                        price: _.round(this.state.price, 5),
                                        discount: _.round(this.state.discount, 2),
                                        vat: _.round(this.state.vat, 2),
                                        total: this.state.total
                                    }));

                                    this.setState({
                                        lines,
                                        product: -1,
                                        warehouse: -1,
                                        amount: 0,
                                        price: 0,
                                        discount: 0,
                                        vat: 0,
                                        total: 0,
                                        canNewLine: false
                                    }, this.validateReceipt);
                                }}
                            >
                                <Icon
                                    style={{ margin: '2px 0px' }}
                                    icon="plus" title="Añadir"
                                />
                            </button>
                        </div>
                    </div>
                    <ReceiptLinesTable
                        editMode={this.props.editMode}
                        receiptLines={this.state.lines}
                        deleteLine={this.deleteLine}
                    />
                    <div className="row stats-zone d-flex justify-content-end" style={{  }} >
                        <div className="col-auto">
                            <span className="label ">{ this.state.orderSubtotal }</span>
                            <span>Subtotal</span>
                        </div>
                        <div className="col-auto">
                            <span className="label ">{ this.state.orderVat }</span>
                            <span>IVA</span>
                        </div>
                        <div className="col-auto">
                            <span className="label ">{ this.state.orderTotal }</span>
                            <span>Total</span>
                        </div>
                    </div>
                    { this.state.modalIsOpen && (
                        <Modal
                            width={modal.width}
                            heading={modal.title}
                            footer={() => modal.footer}
                            autoFocus={false}
                            onClose={this.closeModal}
                        >
                            {modal.body}
                        </Modal>
                    )}
                </div>
            );
        }

        return receiptDetail;
    }
}

const mapStateToProps = (state) => {
    // Reducers
    const purchaseOrdersReducer = state.purchaseOrders;
    const suppliersReducer = state.suppliers;
    const productsReducer = state.products;
    const warehousesReducer = state.warehouses;

    // PurchaseOrders
    const purchaseOrders = purchaseOrdersReducer.get('payload') || List([]);

    // Suppliers
    let suppliersList = suppliersReducer.get('payload') || List([]);
    let suppliers = List([]);

    if (!(suppliersList instanceof List)) {
        suppliersList = List([]);
    }

    suppliers = suppliers.insert(0, Map({
        _id: -1,
        select: 'Selecciona un proveedor'
    }));
    suppliersList.map( supplier => {
        return suppliers = suppliers.insert(suppliers.size,
            supplier.set('select', `[${supplier.get('supplier_code')}] ${supplier.getIn(['basic_info','name'])}`)
        )
    });

    // Warehouses
    let warehouses = warehousesReducer.get('payload') || List([]);

    if (warehouses.size > 0) {
        warehouses = warehouses.map(warehouse => warehouse.set('select', `[${warehouse.get('warehouse_code')}] ${warehouse.get('name')}`));
    }

    warehouses = warehouses.insert(0, Map({
        _id: -1,
        select: 'Selecciona un almacén'
    }));

    // Products
    let products = productsReducer.get('payload') || List([]);

    if (products.size > 0) {
        products = products.map(product => product.set('select', `[${product.get('product_code')}] ${product.get('name')}`));
    }

    products = products.insert(0, Map({
        _id: -1,
        select: 'Selecciona un producto'
    }));

    return {
        purchaseOrders,
        suppliers,
        products,
        warehouses
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getPurchaseOrders: (query) => dispatch(getPurchaseOrders(query)),
        getSuppliers: (query) => dispatch(getSuppliers(query)),
        getProducts: (query) => dispatch(getProducts(query)),
        getWarehouses: (query) => dispatch(getWarehouses(query)),
        cleanPurchaseOrders: () => dispatch(cleanPurchaseOrders()),
        cleanSuppliers: () => dispatch(cleanSuppliers()),
        cleanProducts: () => dispatch(cleanProducts()),
        cleanWarehouses: () => dispatch(cleanWarehouses())
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ReceiptDetail);



// WEBPACK FOOTER //
// ./src/containers/purchases/receipts/ReceiptDetail.js